import React, { useMemo } from 'react';
import Message from './Message';
import ApprovalCard from '../cards/ApprovalCard';
import HumanRequestCard from '../cards/HumanRequestCard';
import { Chat } from '@iqueue/iq-ui';

const RUN_GAP_MS = 5 * 60 * 1000;

function dayLabel(ts) {
  const d = new Date(ts);
  const today = new Date();
  const yesterday = new Date(today.getTime() - 86400000);
  const sameDay = (a, b) => a.toDateString() === b.toDateString();
  if (sameDay(d, today)) return 'Сегодня';
  if (sameDay(d, yesterday)) return 'Вчера';
  return d.toLocaleDateString([], { day: 'numeric', month: 'long' });
}

/**
 * The rows of the conversation — separators, messages, live runs, cards.
 * Renders a fragment INSIDE Chat.Log (the log itself lives in ChatView, as a
 * direct child of Chat.Root — Root buckets parts by element identity).
 *
 * Live runs are rows of the SAME array and the SAME component as messages, so
 * a run that finishes morphs into the reply it produced instead of one node
 * being destroyed and another built (`runKeys`: message_id → runId, filled by
 * the workspace when a message lands for an active run). React reconciles by
 * key WITHIN one children slot, which is why they cannot be two lists.
 */
export default React.memo(function MessageRows({
  messages, runs, runKeys, approvals, humanRequests, hasMore, me,
  onReply, onCancelRun, onRetrySend, onDropMessage, onDecideApproval, onAnswerRequest,
}) {
  // A finished run is NOT dropped here: its row is what the arriving message
  // morphs out of, and the workspace removes it the moment that message lands
  // (or shortly after run.done, when the run produced no message at all).
  const runsArr = useMemo(() => [...(runs ? runs.values() : [])], [runs]);

  // Rows: day separators + messages (author runs broken by sender/time gap) +
  // the live runs, appended in the order they will be replaced.
  const rows = useMemo(() => {
    const out = [];
    let prev = null;
    messages.forEach((m, i) => {
      if (!prev || !isSameDay(prev.ts, m.ts)) out.push({ kind: 'day', ts: m.ts, key: `day-${m.ts}-${i}` });
      const isSystem = m.sender === 'system' || m.type === 'system';
      const next = messages[i + 1];
      const joinsPrev = prev && !isSystem && prev.sender === m.sender && (m.ts - prev.ts) < RUN_GAP_MS
        && isSameDay(prev.ts, m.ts) && prev.type !== 'system' && prev.sender !== 'system';
      const joinsNext = next && !isSystem && next.sender === m.sender && (next.ts - m.ts) < RUN_GAP_MS
        && isSameDay(m.ts, next.ts) && next.type !== 'system' && next.sender !== 'system';
      const position = joinsPrev ? (joinsNext ? 'middle' : 'last') : (joinsNext ? 'first' : 'single');
      const runId = runKeys && m.message_id ? runKeys.get(m.message_id) : undefined;
      const key = runId ? `run-${runId}` : (m.message_id || m.clientTempId || m.id || `${m.ts}-${i}`);
      out.push({ kind: 'msg', msg: m, position, key });
      prev = m;
    });
    for (const run of runsArr) {
      out.push({
        kind: 'msg',
        key: `run-${run.runId}`,
        position: 'single',
        msg: {
          live: true, run, sender: run.agentId, sender_type: 'agent',
          ts: run.startedAt || Date.now(), text: run.text, tools: run.tools,
          progress: run.progress, done: run.done,
        },
      });
    }
    return out;
  }, [messages, runsArr, runKeys]);

  return (
    <>
      {!hasMore && messages.length > 0 && <Chat.Separator label="— начало беседы —" />}
      {rows.map(row => row.kind === 'day'
        ? <Chat.Separator key={row.key} label={dayLabel(row.ts)} />
        : <Message
            key={row.key}
            msg={row.msg}
            me={me}
            position={row.position}
            onReply={onReply}
            onCancelRun={onCancelRun}
            onRetrySend={onRetrySend}
            onDropMessage={onDropMessage}
          />)}
      {(humanRequests || []).map(r => (
        <HumanRequestCard key={r.id} request={r} onAnswer={onAnswerRequest} />
      ))}
      {(approvals || []).map(a => (
        <ApprovalCard key={a.id} approval={a} onDecide={onDecideApproval} />
      ))}
    </>
  );
});

function isSameDay(a, b) {
  return new Date(a).toDateString() === new Date(b).toDateString();
}
